import { useState, useEffect, useRef, useCallback } from 'react';
import { SESSION_CONFIG, SESSION_MESSAGES } from '../config/sessionConfig';

interface UseIdleTimerOptions {
  /**
   * Called when user becomes idle (timeout reached)
   */
  onIdle: () => void;

  /**
   * Called when warning threshold is reached
   */
  onWarning?: () => void;

  /**
   * Whether the timer is enabled (only track when user is authenticated)
   */
  enabled?: boolean;
}

interface UseIdleTimerReturn {
  /**
   * Whether the warning modal should be shown
   */
  isWarning: boolean;

  /**
   * Remaining time in seconds before logout
   */
  remainingTime: number;

  /**
   * Reset the idle timer (user chose to stay logged in)
   */
  resetTimer: () => void;

  /**
   * Get timestamp of last activity
   */
  lastActivity: number;
}

export function useIdleTimer({
  onIdle,
  onWarning,
  enabled = true,
}: UseIdleTimerOptions): UseIdleTimerReturn {
  // State
  const [isWarning, setIsWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(
    Math.floor(SESSION_CONFIG.IDLE_TIMEOUT / 1000)
  );
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Refs for cleanup and throttling
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const throttleRef = useRef<number>(0);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const onIdleRef = useRef(onIdle);
  const onWarningRef = useRef(onWarning);
  const warningShownRef = useRef(false);
  const lastActivityRef = useRef(lastActivity);

  // Keep callback refs up to date
  useEffect(() => {
    onIdleRef.current = onIdle;
    onWarningRef.current = onWarning;
  }, [onIdle, onWarning]);

  // Keep lastActivity ref in sync with state
  useEffect(() => {
    lastActivityRef.current = lastActivity;
  }, [lastActivity]);

  // Update last activity in localStorage and notify other tabs
  const updateLastActivity = useCallback((timestamp: number) => {
    setLastActivity(timestamp);
    setIsWarning(false);
    warningShownRef.current = false;

    // Update localStorage for cross-tab sync (fallback for older browsers)
    try {
      localStorage.setItem(SESSION_CONFIG.LAST_ACTIVITY_KEY, timestamp.toString());
    } catch {
      // Ignore storage errors
    }

    // Notify other tabs via BroadcastChannel
    if (broadcastChannelRef.current) {
      try {
        broadcastChannelRef.current.postMessage({
          type: SESSION_MESSAGES.ACTIVITY,
          timestamp,
        });
      } catch {
        // Channel might be closed
      }
    }
  }, []);

  // Reset the timer (called when user clicks "Stay Logged In")
  const resetTimer = useCallback(() => {
    const now = Date.now();
    updateLastActivity(now);
    setRemainingTime(Math.floor(SESSION_CONFIG.IDLE_TIMEOUT / 1000));

    // Notify other tabs about session extension
    if (broadcastChannelRef.current) {
      try {
        broadcastChannelRef.current.postMessage({
          type: SESSION_MESSAGES.EXTEND,
          timestamp: now,
        });
      } catch {
        // Channel might be closed
      }
    }
  }, [updateLastActivity]);

  // Throttled activity handler
  const handleActivity = useCallback(() => {
    const now = Date.now();

    // Throttle activity updates
    if (now - throttleRef.current < SESSION_CONFIG.ACTIVITY_THROTTLE) {
      return;
    }
    throttleRef.current = now;

    // Update activity timestamp
    updateLastActivity(now);
  }, [updateLastActivity]);

  // Set up BroadcastChannel for cross-tab communication
  useEffect(() => {
    if (!enabled) return;

    // Try to create BroadcastChannel (modern browsers)
    try {
      const channel = new BroadcastChannel(SESSION_CONFIG.BROADCAST_CHANNEL_NAME);
      broadcastChannelRef.current = channel;

      channel.onmessage = (event) => {
        const { type, timestamp } = event.data;

        switch (type) {
          case SESSION_MESSAGES.ACTIVITY:
            // Another tab detected activity, update our state
            setLastActivity(timestamp);
            setIsWarning(false);
            warningShownRef.current = false;
            break;

          case SESSION_MESSAGES.LOGOUT:
            // Another tab logged out, we should too
            onIdleRef.current();
            break;

          case SESSION_MESSAGES.EXTEND:
            // Another tab extended the session
            setLastActivity(timestamp);
            setIsWarning(false);
            warningShownRef.current = false;
            setRemainingTime(Math.floor(SESSION_CONFIG.IDLE_TIMEOUT / 1000));
            break;
        }
      };
    } catch {
      // BroadcastChannel not supported, fall back to storage events
      console.log('[Session] BroadcastChannel not supported, using storage events');
    }

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, [enabled]);

  // Listen for storage events (fallback cross-tab sync)
  useEffect(() => {
    if (!enabled) return;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === SESSION_CONFIG.LAST_ACTIVITY_KEY && event.newValue) {
        const timestamp = parseInt(event.newValue, 10);
        if (!isNaN(timestamp)) {
          setLastActivity(timestamp);
          setIsWarning(false);
          warningShownRef.current = false;
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [enabled]);

  // Set up activity event listeners
  useEffect(() => {
    if (!enabled) return;

    // Add listeners for all activity events
    SESSION_CONFIG.ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Also listen for visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible, check if still logged in from localStorage
        const storedTimestamp = localStorage.getItem(SESSION_CONFIG.LAST_ACTIVITY_KEY);
        if (storedTimestamp) {
          const timestamp = parseInt(storedTimestamp, 10);
          if (!isNaN(timestamp)) {
            setLastActivity(timestamp);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      SESSION_CONFIG.ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, handleActivity]);

  // Initialize last activity from localStorage or set current time
  useEffect(() => {
    if (!enabled) return;

    const storedTimestamp = localStorage.getItem(SESSION_CONFIG.LAST_ACTIVITY_KEY);
    if (storedTimestamp) {
      const timestamp = parseInt(storedTimestamp, 10);
      if (!isNaN(timestamp)) {
        setLastActivity(timestamp);
      }
    } else {
      // No stored timestamp, set current time
      const now = Date.now();
      setLastActivity(now);
      updateLastActivity(now);
    }
  }, [enabled, updateLastActivity]);

  // Main timer check interval
  useEffect(() => {
    if (!enabled) return;

    checkIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastActivityRef.current;
      const remaining = SESSION_CONFIG.IDLE_TIMEOUT - elapsed;
      const remainingSeconds = Math.max(0, Math.floor(remaining / 1000));

      setRemainingTime(remainingSeconds);

      // Check for warning threshold
      if (elapsed >= SESSION_CONFIG.WARNING_THRESHOLD && !warningShownRef.current) {
        warningShownRef.current = true;
        setIsWarning(true);
        onWarningRef.current?.();
      }

      // Check for idle timeout
      if (elapsed >= SESSION_CONFIG.IDLE_TIMEOUT) {
        setIsWarning(false);

        // Notify other tabs about logout
        if (broadcastChannelRef.current) {
          try {
            broadcastChannelRef.current.postMessage({
              type: SESSION_MESSAGES.LOGOUT,
            });
          } catch {
            // Channel might be closed
          }
        }

        onIdleRef.current();
      }
    }, SESSION_CONFIG.CHECK_INTERVAL);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      throttleRef.current = 0;
    };
  }, []);

  return {
    isWarning,
    remainingTime,
    resetTimer,
    lastActivity,
  };
}
